<template>
    <div class="rlms-tooltip-wrapper-" :style="{
        backgroundColor: backgroundColor,
        '--rlms-t-width': width,
        '--rlms-t-border-radius': borderRadius
    }" ref="tooltipRef">
        <!-- Trigger element -->
        <div class="rlms-tooltip-button">
            <slot name="button">hover me</slot>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, h } from "vue";
import tippy from "tippy.js";

const props = defineProps({
    content: { type: String, required: true },
    backgroundColor: { type: String, default: "#fff" },
    placement: { type: String, default: "bottom" },
    trigger: { type: String, default: "mouseenter focus" },
    width: { type: String, default: "250px" },
    borderRadius: { type: String, default: "10px" },
    component: { type: Object, default: null }, // Vue component to render
    componentProps: { type: Object, default: () => ({}) },
    onComponentEvent: { type: Function }
});

const tooltipRef = ref(null);
let tooltipInstance = null;
const emit = defineEmits(['tooltipEvent']);
let appInstance = null;
let componentContainer = null;

function createTooltipContent() {
    const container = document.createElement("div");
    container.innerHTML = props.content;

    if (props.component) {
        componentContainer = document.createElement("div");
        container.appendChild(componentContainer);

        mountComponent();
    }

    return container;
}

async function mountComponent() {
    if (!props.component || !componentContainer) return;

    // Unmount any existing instance
    if (appInstance) {
        appInstance.unmount();
        appInstance = null;
    }

    const { createApp } = await import("vue");

    const wrapper = {
        render() {
            return h(props.component, {
                ...props.componentProps,
                onSaved: (...args) => {
                    emit('tooltipEvent', args?.[0])
                },
            });
        },
    };

    appInstance = createApp(wrapper);
    appInstance.mount(componentContainer);
}

onMounted(async () => {
    await nextTick();

    const contentContainer = createTooltipContent();

    // 3️⃣ Initialize Tippy
    tooltipInstance = tippy(tooltipRef.value.querySelector('.rlms-tooltip-button'), {
        content: contentContainer,
        placement: props.placement,
        trigger: props.trigger,
        arrow: true,
        theme: 'rlms',
        maxWidth: props.width,
        interactive: true,
        allowHTML: true,
        onMount(instance) {
            instance.popper.querySelector('.tippy-box')?.classList.add('rlms-tippy-box');
        }
    });
});

// Watch content updates
watch(
    () => props.content,
    (newContent) => {
        if (tooltipInstance) {
            const container = document.createElement("div");
            container.innerHTML = newContent;
            if (props.component) {
                componentContainer = document.createElement("div");
                container.appendChild(componentContainer);
                mountComponent();
            }
            tooltipInstance.setContent(container);
        }
    }
);

// Watch component props updates (reactive updates)
watch(
    () => props.componentProps,
    () => {
        if (props.component && componentContainer) {
            mountComponent();
        }
    },
    { deep: true }
);

onBeforeUnmount(() => {
    if (tooltipInstance) {
        tooltipInstance.destroy();
        tooltipInstance = null;
    }
    if (appInstance) {
        appInstance.unmount();
        appInstance = null;
    }
});
</script>

<style>
/* Custom RLMS tooltip theme */
.tippy-box[data-theme~="rlms"] {
    background-color: #ffffff;
    color: #333;
    border-radius: var(--rlms-t-border-radius, 10px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    max-width: var(--rlms-t-width, 250px);
    padding: 0;
    font-size: 14px;
}

.tippy-box {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
    border-radius: 12px !important;
}

.tippy-box.rlms-tippy-box .tippy-content {
    box-shadow: none !important;
    padding: 8px 12px !important;
}

.tippy-arrow {
    color: #dee2e7 !important;
}

.tippy-box.rlms-tippy-box>.tippy-arrow:before {
    border-left-color: #dee2e7 !important;
}

.tippy-box.rlms-tippy-box[data-animation="fade"][data-state="hidden"] {
    opacity: 0;
}

.tippy-box.rlms-tippy-box {
    position: relative;
    background-color: #fff;
    color: #fff;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
    white-space: normal;
    outline: 0;
    transition-property: transform, visibility, opacity;
}

.tippy-box.rlms-tippy-box[data-placement^="top"]>.tippy-arrow {
    bottom: 0;
}

.tippy-box.rlms-tippy-box[data-placement^="top"]>.tippy-arrow:before {
    bottom: -7px;
    left: 0;
    border-width: 8px 8px 0;
    border-top-color: initial;
    transform-origin: center top;
}

.tippy-box.rlms-tippy-box[data-placement^="bottom"]>.tippy-arrow {
    top: 0;
}

.tippy-box.rlms-tippy-box[data-placement^="bottom"]>.tippy-arrow:before {
    top: -7px;
    left: 0;
    border-width: 0 8px 8px;
    border-bottom-color: initial;
    transform-origin: center bottom;
}

.tippy-box.rlms-tippy-box[data-placement^="left"]>.tippy-arrow {
    right: 0;
}

.tippy-box.rlms-tippy-box[data-placement^="left"]>.tippy-arrow:before {
    border-width: 8px 0 8px 8px;
    border-left-color: initial;
    right: -7px;
    transform-origin: center left;
}

.tippy-box.rlms-tippy-box[data-placement^="right"]>.tippy-arrow {
    left: 0;
}

.tippy-box.rlms-tippy-box[data-placement^="right"]>.tippy-arrow:before {
    left: -7px;
    border-width: 8px 8px 8px 0;
    border-right-color: initial;
    transform-origin: center right;
}

.tippy-box.rlms-tippy-box[data-inertia][data-state="visible"] {
    transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
}
</style>
